import React from 'react';
import { useLocation } from 'react-router-dom';
import SEO from './SEO';

/**
 * SEO.2 — Route-driven SEO.
 *
 * Mount ONCE inside the router. Looks up the current pathname in ROUTE_SEO and
 * emits per-page <title>, description, canonical, OG/Twitter meta and optional
 * JSON-LD via <SEO />. Pages can still layer in their own <SEO /> if they need
 * dynamic content (e.g. Situations Hub detail pages).
 */

// Static routes. For dynamic routes we use exact matches and fall back to the
// SeriesPage (`/:seriesSlug`) which is handled by the catch-all logic below.
const ROUTE_SEO = {
  '/': {
    title: '30 Day Skills — Leadership books & AI Companion for IT managers',
    description:
      'Practical leadership training for IT managers. Read the 30-Day Skills book series, use the AI Companion for daily reflection, and grow a real Living CV of demonstrated capability.',
  },
  '/companion': {
    title: 'AI Career Companion for IT Managers | 30 Day Skills',
    description:
      'An AI coach who remembers. Daily reflection, capability tracking, and a Living CV that shows what you actually did — not what you say you did.',
    type: 'website',
  },
  '/clarity': {
    title: 'Situational Clarity — Get unstuck in 60 seconds | 30 Day Skills',
    description:
      'Describe your situation. Get a structured take: what is really going on, what to do next, and how to build the habit.',
  },
  '/review': {
    title: 'Free Manager Review — See how you actually lead | 30 Day Skills',
    description:
      'A 2-minute reflection produces a personalised AI review showing your leadership strengths, blind spots, and the next capability to build.',
  },
  '/visibility': {
    title: 'Get visible without office politics | 30 Day Skills',
    description:
      'Practical, structured ways for IT managers to be seen and heard — without becoming the person no one trusts.',
  },
  '/insight': {
    title: 'Real insight into how you lead | 30 Day Skills',
    description:
      "Understand what patterns your leadership actually shows — and what one shift would matter most this quarter.",
  },
  '/career': {
    title: 'Career growth for IT managers | 30 Day Skills',
    description:
      'Move from "delivering the work" to being ready for the next role — with evidence, not vibes.',
  },
  '/overwhelm': {
    title: 'Feeling overwhelmed at work? Here is how to reset | 30 Day Skills',
    description:
      'For IT managers running hot: a structured 15-minute reset that identifies the one thing to change this week — with AI-generated action steps.',
  },
  '/teams': {
    title: 'Leadership OS for teams | 30 Day Skills',
    description:
      'Give every manager in your organisation the same structured leadership habits — with privacy-preserving team signals.',
  },
  '/terms': {
    title: 'Terms of Use | 30 Day Skills',
    description: 'Terms of Use for the 30 Day Skills platform and AI Companion.',
    noIndex: false,
  },
  '/privacy': {
    title: 'Privacy Policy | 30 Day Skills',
    description:
      "How 30 Day Skills collects, uses, and protects your data. Includes your GDPR rights and how to exercise them.",
  },
  '/terms-of-sale': {
    title: 'Terms of Sale | 30 Day Skills',
    description: 'Terms of Sale for books and Companion subscriptions.',
  },
  '/companion-terms': {
    title: 'Companion Terms | 30 Day Skills',
    description: 'Terms governing your use of the AI Companion.',
  },
  '/companion-privacy': {
    title: 'Companion Privacy | 30 Day Skills',
    description: "How the AI Companion handles your reflections and conversations.",
  },
  '/data-request': {
    title: 'Your data, your rights | 30 Day Skills',
    description:
      'Exercise your UK GDPR rights: access, rectification, erasure, portability, and more. We respond within 30 days.',
  },
  '/situations': {
    title: 'Situations Hub — Real IT leadership answers | 30 Day Skills',
    description:
      'Concise, practical answers to the situations IT managers actually face — missed deadlines, difficult conversations, new-manager challenges and more.',
  },
  '/login': {
    title: 'Sign in | 30 Day Skills',
    description: 'Sign in to 30 Day Skills.',
    noIndex: true,
  },
  '/redeem': {
    title: 'Redeem your code | 30 Day Skills',
    description: 'Redeem a book or Companion access code.',
    noIndex: true,
  },
};

// Prefix-matched patterns for grouped routes. Ordered — first match wins.
const PREFIX_SEO = [
  {
    prefix: '/auth/',
    seo: { title: 'Signing you in | 30 Day Skills', description: 'One moment while we verify your sign-in link.', noIndex: true },
  },
  {
    prefix: '/r/',
    seo: { title: 'Companion deep-link | 30 Day Skills', description: 'Loading your Companion context.', noIndex: true },
  },
  {
    prefix: '/dashboard',
    seo: { title: 'Dashboard | 30 Day Skills', description: 'Your Living CV, reflections, and Companion.', noIndex: true },
  },
  {
    prefix: '/admin',
    seo: { title: 'Admin | 30 Day Skills', description: 'Admin console.', noIndex: true },
  },
  {
    prefix: '/onboarding',
    seo: { title: 'Get started | 30 Day Skills', description: 'A short setup to personalise your Companion.', noIndex: true },
  },
  {
    prefix: '/teams/',
    seo: { title: 'Teams | 30 Day Skills', description: 'Team invitations and management.' },
  },
  {
    prefix: '/situations/',
    // Detail pages set their own SEO via <SEO /> in the component — fallback below.
    seo: { title: 'Situations Hub | 30 Day Skills', description: 'A specific leadership situation and how to handle it.' },
  },
];

const SERIES_SEO = {
  'it-leadership': {
    title: '30-Day Leadership Skill Builder — Books for IT managers',
    description:
      'Practical, day-by-day leadership skills for IT managers. Books 1–4 available. Companion integration for reflection and Living CV.',
  },
  'healthcare-it': {
    title: 'Healthcare IT Leadership — 30-Day Skill Series',
    description:
      'Leadership training built for healthcare IT — where clinical safety, regulatory pressure, and delivery all collide.',
  },
};

const KNOWN_SERIES = new Set(Object.keys(SERIES_SEO));

const matchRoute = (pathname) => {
  // Exact static match
  if (ROUTE_SEO[pathname]) return ROUTE_SEO[pathname];

  // Strip trailing slash and retry
  const trimmed = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  if (ROUTE_SEO[trimmed]) return ROUTE_SEO[trimmed];

  // Complete-value pages fall under /review, /visibility etc. — reuse parent copy
  const completeMatch = trimmed.match(/^(\/(?:review|visibility|insight|career|overwhelm|clarity))\/complete$/);
  if (completeMatch && ROUTE_SEO[completeMatch[1]]) return ROUTE_SEO[completeMatch[1]];

  // Prefix matches
  for (const { prefix, seo } of PREFIX_SEO) {
    if (trimmed.startsWith(prefix)) return seo;
  }

  // /:seriesSlug — the series page (catch-all just below the static routes)
  const seriesMatch = trimmed.match(/^\/([a-z0-9-]+)$/);
  if (seriesMatch) {
    const slug = seriesMatch[1];
    if (SERIES_SEO[slug] || KNOWN_SERIES.has(slug)) {
      return SERIES_SEO[slug] || {
        title: `${slug.replace(/-/g, ' ')} | 30 Day Skills`,
        description: 'Leadership training for managers.',
      };
    }
    // Unknown top-level slug → default homepage SEO with noIndex to avoid
    // Google indexing every 404-ish permutation.
    return { ...ROUTE_SEO['/'], noIndex: true };
  }

  return ROUTE_SEO['/'];
};

const RouteSEO = () => {
  const location = useLocation();
  const seoConfig = matchRoute(location.pathname);
  return <SEO {...seoConfig} path={location.pathname} />;
};

export default RouteSEO;
